import { useMemo, useState } from "react";
import { formatAmount, formatMoney, methodLabel } from "../lib/format";

/**
 * Charts.
 *
 * Two deliberate decisions govern this file.
 *
 * First, every chart here plots a single measure, so every chart uses a single hue.
 * Categorical colour would be decoration, not information.
 *
 * Second, payment outcomes are never encoded as adjacent colour. Succeeded and failed
 * are the most consequential distinction on the page, and the green/red pair a reader
 * expects separates by only ΔE 7.6 under deuteranopia — around one in twelve men would
 * be reading the most important thing on the screen by a colour they cannot reliably
 * tell apart. Outcomes are therefore shown as labelled figures, not as coloured
 * segments of a bar.
 *
 * The mark hue below (#2f9268) was chosen by running the palette validator against
 * the paper surface rather than by eye: it clears the lightness band, the chroma
 * floor and 3:1 contrast.
 */

const MARK = "#2f9268";
const MARK_SOFT = "#cfe4da";

/* -------------------------------------------------------------------------- */
/* Daily volume                                                                */
/* -------------------------------------------------------------------------- */

export interface DailyPoint {
  day: string;
  gross_minor: number;
  succeeded_count: number;
  failed_count: number;
}

export function VolumeChart({
  data,
  currency,
  height = 168,
}: {
  data: DailyPoint[];
  currency: string;
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);

  const max = useMemo(() => Math.max(1, ...data.map((d) => d.gross_minor)), [data]);
  const total = useMemo(() => data.reduce((sum, d) => sum + d.gross_minor, 0), [data]);

  if (data.length === 0) {
    return <p className="px-5 py-10 text-center text-[13px] text-ink-muted">No activity yet.</p>;
  }

  const peak = data.reduce((best, d, i) => (d.gross_minor > (data[best]?.gross_minor ?? 0) ? i : best), 0);

  return (
    <div>
      <div className="flex items-baseline justify-between px-5 pt-4">
        <div>
          <p className="label">Collected · last {data.length} days</p>
          <p className="mt-1 font-display text-[26px] leading-none tabular">
            {formatMoney(total, currency)}
          </p>
        </div>
        <button
          onClick={() => setShowTable((v) => !v)}
          className="text-[11.5px] text-ink-muted underline underline-offset-2 transition-colors hover:text-ink"
          aria-expanded={showTable}
        >
          {showTable ? "Show chart" : "Show table"}
        </button>
      </div>

      {showTable ? (
        <DailyTable data={data} currency={currency} />
      ) : (
        <div className="relative px-5 pb-3 pt-5">
          {/* The bars are laid out in HTML rather than in a scaled SVG viewBox.
              A viewBox with preserveAspectRatio="none" stretches horizontally, which
              would distort the rounded data-ends into ellipses and make the 2px gaps
              vary with the container width. In flow layout both stay exact. */}
          <div
            className="flex items-end gap-[2px]"
            style={{ height }}
            role="img"
            aria-label={`Daily collected volume in ${currency} over the last ${data.length} days, totalling ${formatMoney(total, currency)}`}
          >
            {data.map((d, i) => {
              const ratio = d.gross_minor / max;
              const barHeight = d.gross_minor > 0 ? Math.max(ratio * (height - 14), 3) : 2;
              const active = hover === i;
              return (
                <div
                  key={d.day}
                  className="group relative flex h-full flex-1 cursor-pointer items-end"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  tabIndex={0}
                  aria-label={`${longDay(d.day)}: ${formatMoney(d.gross_minor, currency)}`}
                >
                  <div
                    className="w-full transition-opacity duration-150"
                    style={{
                      height: barHeight,
                      // 4px rounded data-ends, anchored square to the baseline.
                      borderRadius: "4px 4px 0 0",
                      background: d.gross_minor > 0 ? MARK : MARK_SOFT,
                      opacity: hover === null || active ? 1 : 0.4,
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* A recessive baseline beneath the marks. */}
          <div className="h-px w-full bg-rule-strong" aria-hidden="true" />

          {/* Direct labels, selectively: the first day, the peak and the last day.
              A number on every bar would be unreadable and unnecessary. */}
          <div className="relative mt-1.5 h-4">
            {[0, peak, data.length - 1]
              .filter((i, idx, arr) => arr.indexOf(i) === idx)
              .map((i) => {
                const point = data[i];
                if (!point) return null;
                const left = ((i + 0.5) / data.length) * 100;
                return (
                  <span
                    key={i}
                    className="absolute -translate-x-1/2 whitespace-nowrap text-[10.5px] text-ink-faint"
                    style={{ left: `${Math.min(Math.max(left, 6), 94)}%` }}
                  >
                    {shortDay(point.day)}
                  </span>
                );
              })}
          </div>

          {hover !== null && data[hover] && (
            <VolumeTooltip point={data[hover]!} currency={currency} index={hover} count={data.length} />
          )}
        </div>
      )}
    </div>
  );
}

function VolumeTooltip({
  point,
  currency,
  index,
  count,
}: {
  point: DailyPoint;
  currency: string;
  index: number;
  count: number;
}) {
  // The tooltip flips to the other side near the right edge so it is never clipped.
  const left = ((index + 0.5) / count) * 100;
  const flip = left > 65;

  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute top-2 z-10 min-w-[150px] rounded-md border border-rule-strong bg-paper-raised px-3 py-2 shadow-lift"
      style={flip ? { right: `${100 - left + 2}%` } : { left: `${left + 2}%` }}
    >
      <p className="text-[11px] font-medium text-ink-muted">{longDay(point.day)}</p>
      <p className="mt-0.5 font-mono text-[14px] tabular text-ink">
        {formatMoney(point.gross_minor, currency)}
      </p>
      <p className="mt-1 text-[11.5px] text-ink-muted">
        <span className="tabular">{point.succeeded_count}</span> succeeded
        {point.failed_count > 0 && (
          <>
            {" · "}
            <span className="tabular">{point.failed_count}</span> failed
          </>
        )}
      </p>
    </div>
  );
}

function DailyTable({ data, currency }: { data: DailyPoint[]; currency: string }) {
  // The table view is the accessible equivalent of the chart, and the relief the
  // palette validator requires for any mark whose contrast only warns.
  return (
    <div className="max-h-[220px] overflow-y-auto px-5 pb-4 pt-3">
      <table className="w-full text-[12.5px]">
        <caption className="sr-only">Daily collected volume</caption>
        <thead className="sticky top-0 bg-paper-raised">
          <tr>
            <th className="label border-b border-rule py-1.5 text-left">Day</th>
            <th className="label border-b border-rule py-1.5 text-right">Collected</th>
            <th className="label border-b border-rule py-1.5 text-right">Succeeded</th>
            <th className="label border-b border-rule py-1.5 text-right">Failed</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.day}>
              <td className="border-b border-rule py-1.5">{longDay(d.day)}</td>
              <td className="border-b border-rule py-1.5 text-right font-mono tabular">
                {formatAmount(d.gross_minor)} {currency}
              </td>
              <td className="border-b border-rule py-1.5 text-right tabular">{d.succeeded_count}</td>
              <td className="border-b border-rule py-1.5 text-right tabular">{d.failed_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Payment method breakdown                                                    */
/* -------------------------------------------------------------------------- */

export interface MethodPoint {
  method: string;
  count: number;
  gross_minor: number;
}

/**
 * A ranked horizontal bar chart.
 *
 * Horizontal because the rail names are long — "Visa / Mastercard" will not sit under
 * a vertical bar — and ranked because the question a merchant is asking is "which rail
 * do my customers actually use", which is an ordering question.
 */
export function MethodBreakdown({ data, currency }: { data: MethodPoint[]; currency: string }) {
  const rows = useMemo(() => data.filter((d) => d.gross_minor > 0), [data]);
  const max = useMemo(() => Math.max(1, ...rows.map((d) => d.gross_minor)), [rows]);

  if (rows.length === 0) {
    return (
      <p className="px-5 py-10 text-center text-[13px] text-ink-muted">
        No completed payments in this period.
      </p>
    );
  }

  return (
    <ul className="space-y-2.5 px-5 py-4">
      {rows.map((row) => (
        <li key={row.method}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span className="truncate text-[12.5px] text-ink">{methodLabel(row.method)}</span>
            {/* Every bar is directly labelled with its value, so the chart never
                depends on a reader estimating a length against an axis. */}
            <span className="shrink-0 font-mono text-[12px] tabular text-ink-muted">
              {formatAmount(row.gross_minor)} {currency}
              <span className="ml-2 text-ink-faint">×{row.count}</span>
            </span>
          </div>
          <div className="h-[6px] w-full overflow-hidden rounded-full bg-paper-sunk">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{ width: `${Math.max((row.gross_minor / max) * 100, 2)}%`, background: MARK }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------- */
/* Stat tiles                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A single figure with its label.
 *
 * This is the form the data actually calls for: one number, read once. Wrapping it in
 * a chart would add ink without adding meaning.
 */
export function StatTile({
  label,
  value,
  detail,
  accent = "ink",
}: {
  label: string;
  value: string;
  detail?: string;
  accent?: "ink" | "verdigris" | "brass";
}) {
  const colour =
    accent === "verdigris"
      ? "text-verdigris"
      : accent === "brass"
        ? "text-brass"
        : "text-ink";

  return (
    <div className="px-5 py-4">
      <p className="label">{label}</p>
      <p className={`mt-1.5 font-display text-[27px] leading-none tabular ${colour}`}>{value}</p>
      {detail && <p className="mt-1.5 text-[12px] text-ink-muted">{detail}</p>}
    </div>
  );
}

/**
 * A success rate, shown as a figure with a proportional rule beneath it.
 *
 * The rule is a single mark, not a two-colour split: it shows the proportion that
 * succeeded without asking the reader to distinguish green from red.
 */
export function SuccessRate({ succeeded, failed }: { succeeded: number; failed: number }) {
  const total = succeeded + failed;
  const rate = total === 0 ? null : (succeeded / total) * 100;

  return (
    <div className="px-5 py-4">
      <p className="label">Success rate</p>
      <p className="mt-1.5 font-display text-[27px] leading-none tabular text-ink">
        {rate === null ? "—" : `${rate.toFixed(1)}%`}
      </p>
      <div className="mt-2.5 h-[5px] w-full overflow-hidden rounded-full bg-paper-sunk">
        <div
          className="h-full rounded-full transition-[width] duration-700"
          style={{ width: `${rate ?? 0}%`, background: MARK }}
        />
      </div>
      <p className="mt-1.5 text-[12px] text-ink-muted">
        <span className="tabular">{succeeded.toLocaleString()}</span> succeeded
        {failed > 0 && (
          <>
            {" · "}
            <span className="tabular">{failed.toLocaleString()}</span> did not
          </>
        )}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function shortDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(date);
}

function longDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(date);
}
